/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { ReactNode } from "react";
import { FadeIn } from "./primitives/fade-in";

interface MarketingSectionHeaderProps {
  badge?: string;
  title: string | ReactNode;
  description?: string;
  centered?: boolean;
  className?: string;
  /** Optional stable id for the h2 so parent section can use aria-labelledby */
  id?: string;
}

/**
 * Shared section header for marketing pages
 * Consistent typography and spacing
 */
export function MarketingSectionHeader({
  badge,
  title,
  description,
  centered = true,
  className = "",
  id: explicitId,
}: MarketingSectionHeaderProps) {
  const headingId =
    explicitId ??
    (typeof title === "string"
      ? title.toLowerCase().replace(/[^a-z0-9]+/g, "-")
      : "section-heading");

  return (
    <FadeIn
      as="header"
      className={`mb-8 ${centered ? "text-center" : ""} ${className}`}
    >
      {badge && (
        <div className="mb-4">
          <span className="section-badge">{badge}</span>
        </div>
      )}

      <h2
        id={headingId}
        className="text-[clamp(32px,5vw,56px)] leading-[1.1] font-bold font-display text-foreground mb-4"
      >
        {title}
      </h2>

      {description && (
        <p className="text-base text-muted-foreground max-w-2xl mx-auto">
          {description}
        </p>
      )}
    </FadeIn>
  );
}
